import { SidePanelLayerType } from "@nova/constants"
import VideoLayer from "features/map/layers/video/Video.class"
import { useAppSelector } from "integrations/redux/hooks"
import store from "integrations/redux/store"
import { PluginSpec } from "plugins/plugin-registry"
import { useMemo, useState } from "react"
import {
    useMultipleVideoData,
    useMultipleCurrentFrameIndex,
    useMultipleHasPlayedVideo,
} from "../controllers/hooks/useMultipleControllerEvents"
import { VideoControllerFactory } from "../controllers/VideoControllerFactory"
import {
    selectDismissedVideoFlights,
    selectOpenVideoFlights,
    selectFlightPathEnabledMap,
    selectDroneIconEnabledMap,
    selectCrosshairEnabledMap,
    selectFrameEnabledMap,
} from "../state/selectors"
import { selectPictureInPictureId } from "plugins/shared/playback/state/selectors"
import { LiveVideoFlight, VideoFlight } from "@nova/protobuf"
import { get } from "nodash"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"

export function getUseVideoFlightPath<T extends VideoFlight | LiveVideoFlight>(
    spec: PluginSpec<T>,
    opts?: { allowCrosshair?: boolean; allowFrame?: boolean }
) {
    const useVideoFlightPath = () => {
        const [layers, setLayers] = useState<VideoLayer[]>([])
        const openFlightIds = useAppSelector(selectOpenVideoFlights)
        const dismissedFlightIds = useAppSelector(selectDismissedVideoFlights)
        const flights = useAppSelector(spec.selectAllEntities)
        const flightIds = useMemo(() => {
            const s = new Set<string>()
            openFlightIds.forEach((id) => s.add(id))
            flights.forEach((flight) => {
                if (get(flight, "live") && !dismissedFlightIds.includes(flight.id)) {
                    s.add(flight.id)
                }
            })
            return Array.from(s)
        }, [dismissedFlightIds, flights, openFlightIds])
        const { vals: flightData, key: flightDataKey } = useMultipleVideoData({ flightIds })
        const { vals: currentFrameIndices, key: currentFrameIndicesKey } =
            useMultipleCurrentFrameIndex({ flightIds })
        const { vals: hasPlayedMap, key: hasPlayedMapKey } = useMultipleHasPlayedVideo({
            flightIds,
        })
        const pictureInPictureId = useAppSelector(selectPictureInPictureId)
        const flightPathEnabledMap = useAppSelector(selectFlightPathEnabledMap)
        const droneIconEnabledMap = useAppSelector(selectDroneIconEnabledMap)
        const crosshairEnabledMap = useAppSelector(selectCrosshairEnabledMap)
        const frameEnabledMap = useAppSelector(selectFrameEnabledMap)
        useEffectUnsafe(() => {
            // IMPORTANT: remember to update the dep array when changing this effect.
            // !!! DO NOT put any map value returned from any useMultiple hook as a dep. Instead, just put the key.
            // Putting the actual map can cause infinite rerenders.
            let isMounted = true
            const generateLayers = async () => {
                const state = store.getState()
                const layerPromises = Object.entries(flightData)
                    .filter(([flightId]) => !pictureInPictureId || pictureInPictureId === flightId)
                    .reduce((acc, [flightId, currentFlightData]) => {
                        const entity = spec.selectSingleEntity(flightId)(state)
                        if (!entity) {
                            return acc
                        }
                        const getLayer = async () => {
                            const controller =
                                await VideoControllerFactory.getOrCreateControllerForFlight({
                                    flightId,
                                })
                            const data = currentFlightData
                                .filter(
                                    (e) =>
                                        e.longitude &&
                                        Math.abs(e.longitude) > 0 &&
                                        e.latitude &&
                                        Math.abs(e.latitude) > 0
                                )
                                .map((e) => [
                                    e.longitude,
                                    e.latitude,
                                    e.elevation,
                                    e.pitch,
                                    e.yaw,
                                    e.roll,
                                    e.fov,
                                    e.height,
                                ])
                            const hasPlayed = hasPlayedMap[flightId]
                            const flightPathEnabled = flightPathEnabledMap[flightId] !== false // treat undefined as true
                            const droneEnabled = droneIconEnabledMap[flightId] !== false // treat undefined as true
                            const crosshairEnabled =
                                opts?.allowCrosshair && crosshairEnabledMap[flightId] !== false // treat undefined as true
                            const frameEnabled =
                                opts?.allowFrame && frameEnabledMap[flightId] !== false // treat undefined as true
                            return new VideoLayer({
                                id: "live-video-layer" + flightId,
                                data,
                                flightId,
                                visible: hasPlayed,
                                distance: 75,
                                flightPathEnabled,
                                droneEnabled,
                                crosshairEnabled,
                                frameEnabled,
                                loading: false,
                                frameNumber: currentFrameIndices[flightId],
                                mediaType: get(entity, "live") ? "image" : "video",
                                live:
                                    spec.sidePanelLayerType === SidePanelLayerType.LiveVideoFlight,
                                color: controller?.getDroneIconColor(),
                                getVideoElement: () => controller?.getVideoElement() || null,
                            })
                        }
                        acc.push(getLayer())
                        return acc
                    }, [] as Promise<VideoLayer>[])
                const resolvedLayers = await Promise.all(layerPromises)
                if (isMounted) {
                    setLayers(resolvedLayers)
                }
            }
            void generateLayers()
            return () => {
                isMounted = false
            }
        }, [
            crosshairEnabledMap,
            currentFrameIndicesKey,
            droneIconEnabledMap,
            flightDataKey,
            flightPathEnabledMap,
            frameEnabledMap,
            hasPlayedMapKey,
            pictureInPictureId,
        ])
        return layers
    }
    return useVideoFlightPath
}
